import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import MetaTags from "react-meta-tags";
import BootstrapTable from "react-bootstrap-table-next";
import Select from "react-select";
import { updatePayment, deletePayment } from "store/Payment/actions";
import { getInstrumentReportLab } from "store/databaseofunits/actions";
import { Card, CardBody, Col, Container, Row, Label } from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb";
import { getParticipantPayment } from "store/Payment/actions";
import "assets/scss/table.scss";

// ───────── Date helpers ─────────
// Formats to DD-MMM-YY, e.g. "02-Sep-26"
const MONTH_ABBR = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const formatDate = value => {
  if (!value) return "NS";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "NS";

  const day = String(d.getDate()).padStart(2, "0");
  const month = MONTH_ABBR[d.getMonth()];
  const year = String(d.getFullYear()).slice(-2);

  return `${day}-${month}-${year}`;
};

class ParticipantPayments extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // ───────── Filters (cascade: City → Type → Participant) ─────────
      cityFilter: "",
      typeFilter: "",
      participantFilter: "",

      // ───────── Data ─────────
      participants: [], // raw list straight from the API (flat: each has .results[])

      user_id: null,
      organization_name: null,
    };

    this.equipmentTableColumns = [
      {
        dataField: "participantName",
        text: "Participant",
        sort: true,
        style: { textAlign: "left" },
        formatter: cell => cell || "NS",
      },
      {
        dataField: "instrument_type",
        text: "Equipment Type",
        sort: true,
        style: { textAlign: "left" },
        formatter: cell => cell || "NS",
      },
      {
        dataField: "name",
        text: "Equipment",
        sort: true,
        style: { textAlign: "left" },
        formatter: cell => cell || "NS",
      },
      {
        dataField: "manufacturer",
        text: "Manufacturer",
        sort: true,
        style: { textAlign: "left" },
        formatter: cell => cell || "NS",
      },
      {
        // Count of DISTINCT rounds the equipment participated in.
        dataField: "round_count",
        text: "Rounds Participated",
        sort: true,
        style: { textAlign: "center" },
        formatter: (cell, row) =>
          cell != null ? (
            <span
              role="button"
              tabIndex={0}
              className="text-primary text-decoration-underline"
              style={{ cursor: "pointer" }}
              onClick={() => this.openRoundsPage(row)}
              onKeyPress={e => {
                if (e.key === "Enter") this.openRoundsPage(row);
              }}
            >
              {cell}
            </span>
          ) : (
            "NS"
          ),
      },
      {
        dataField: "last_round_date",
        text: "Last Round Date",
        sort: true,
        style: { textAlign: "center" },
        formatter: cell => formatDate(cell),
        sortFunc: (a, b, order) => {
          const timeA = a ? new Date(a).getTime() : 0;
          const timeB = b ? new Date(b).getTime() : 0;
          return order === "asc" ? timeA - timeB : timeB - timeA;
        },
      },
      {
        // Count of distinct analytes submitted within the most recent
        // round for this equipment. Clicking it opens the rounds page,
        // scoped to just that last round.
        dataField: "last_round_count",
        text: "Last Round Count",
        sort: true,
        style: { textAlign: "center" },
        formatter: (cell, row) =>
          cell != null ? (
            <span
              role="button"
              tabIndex={0}
              className="text-primary text-decoration-underline"
              style={{ cursor: "pointer" }}
              onClick={() => this.openLastRoundPage(row)}
              onKeyPress={e => {
                if (e.key === "Enter") this.openLastRoundPage(row);
              }}
            >
              {cell}
            </span>
          ) : (
            "NS"
          ),
      },
    ];

    // ───────── Default sort: Participant, A → Z ─────────
    this.defaultSorted = [
      {
        dataField: "participantName",
        order: "asc",
      },
    ];
  }

  componentDidMount() {
    const savedFilters = localStorage.getItem("participantPaymentsFilters");
    if (savedFilters) {
      try {
        const parsed = JSON.parse(savedFilters);
        this.setState({ ...parsed });
      } catch (e) {
        console.error("Failed to parse saved filters:", e);
      }
    }

    const { onGetParticipantpayment, onGetInstrumentReportList } = this.props;

    onGetParticipantpayment();

    const authUser = localStorage.getItem("authUser");
    const user_id = authUser ? JSON.parse(authUser).user_id : null;

    if (user_id) {
      this.setState({ user_id }, () => {
        const { organization_name } = this.props.match.params || {};

        if (organization_name) {
          this.setState({ organization_name }, () => {
            onGetInstrumentReportList(user_id);
          });
        }
      });
    }
  }

  componentWillUnmount() {
    localStorage.removeItem("participantPaymentsFilters");
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.InstrumentReportLabDetail !==
      prevProps.InstrumentReportLabDetail
    ) {
      const participants =
        this.props.InstrumentReportLabDetail?.participants || [];
      this.setState({ participants });
    }
  }

  persistFilters = () => {
    const { cityFilter, typeFilter, participantFilter } = this.state;
    localStorage.setItem(
      "participantPaymentsFilters",
      JSON.stringify({ cityFilter, typeFilter, participantFilter })
    );
  };

  handleCityChange = selected => {
    const value = selected?.value === "All" ? "" : selected?.value || "";
    this.setState(
      { cityFilter: value, typeFilter: "", participantFilter: "" },
      this.persistFilters
    );
  };

  handleTypeChange = selected => {
    const value = selected?.value === "All" ? "" : selected?.value || "";
    this.setState(
      { typeFilter: value, participantFilter: "" },
      this.persistFilters
    );
  };

  handleParticipantChange = selected => {
    const value = selected?.value === "All" ? "" : selected?.value || "";
    this.setState({ participantFilter: value }, this.persistFilters);
  };

  // ───────── Flatten ALL of a participant's results into round rows ─────────
  // Unlike groupResultsByEquipment (which buckets rows per equipment for
  // the summary table on THIS page), this keeps every result row for the
  // lab as-is — every scheme, every round, every piece of equipment —
  // each tagged with its own instrument_name/instrument_id. This is what
  // feeds the Rounds Participated page so that page shows the lab's full
  // record, not just the rounds run on whichever equipment was clicked.
  getAllRoundsForParticipant = participant => {
    return (participant.results || []).map(r => ({
      id: `${r.round_id}`,
      name: r.round_name,
      lab_id: participant.id,
      scheme_name: r.scheme_name,
      cycle_no: r.cycle_no,
      round_no: r.round_no,
      analyte_name: r.analyte_name,
      unit: r.unit,
      method: r.method,
      reagent: r.reagent,
      evaluation_status: r.evaluation,
      is_qualitative: r.is_qualitative,
      round_start_date: r.round_start_date,
      round_closing_date: r.round_closing_date,
      round_status: r.round_status,
      instrument_name: r.instrument_name,
      instrument_id: r.instrument_id,
    }));
  };

  // ───────── Navigate to the Rounds Participated page ─────────
  // Sends the LAB's full record — every scheme/round across every piece
  // of equipment — regardless of which equipment row was clicked, so the
  // Rounds Participated page can show a complete picture with its own
  // Equipment column per round.
  openRoundsPage = row => {
    console.log("Opening Rounds Page - lab_id:", row.lab_id);

    const participant = this.state.participants.find(
      p => String(p.id) === String(row.lab_id)
    );

    const allRounds = participant
      ? this.getAllRoundsForParticipant(participant)
      : row.rounds || [];

    this.props.history.push("/equipment-report/rounds", {
      modalType: "participated",
      equipmentName: "All Equipment",
      labName: row.participantName || "NS",
      lab_id: row.lab_id,
      rounds: allRounds,
    });
  };

  // Same page, scoped to only the round that produced "last_round_count"
  // (i.e. the most recent round, by closing_date, this specific equipment
  // submitted results in). Left scoped to one equipment intentionally —
  // only the "Rounds Participated" click (openRoundsPage) was asked to
  // show the full record.
  openLastRoundPage = row => {
    const lastRoundRows = (row.rounds || []).filter(
      r => String(r.id) === String(row.last_round_id)
    );

    console.log("Opening Last Round Page - lab_id:", row.lab_id);

    this.props.history.push("/equipment-report/rounds", {
      modalType: "lastRound",
      equipmentName: row.name || "NS",
      labName: row.participantName || "NS",
      lab_id: row.lab_id,
      rounds: lastRoundRows,
    });
  };
  // ───────── Option builders (each narrows based on the ones above it) ─────────
  // NOTE: API now returns `city` instead of `district`. `type` is no longer
  // part of the flat participant payload — if you still need the Type
  // filter, add `type` back into the participants[] response server-side;
  // until then this returns no options and the filter is effectively inert.

  getCityOptions = () => {
    const { participants } = this.state;
    const cities = new Set();
    participants.forEach(p => {
      if (p.city) cities.add(p.city);
    });
    return Array.from(cities)
      .sort()
      .map(city => ({ value: city, label: city }));
  };

  getTypeOptions = () => {
    const { participants, cityFilter } = this.state;
    const types = new Set();
    participants
      .filter(p => (cityFilter ? p.city === cityFilter : true))
      .forEach(p => {
        if (p.type) types.add(p.type);
      });
    return Array.from(types)
      .sort()
      .map(type => ({ value: type, label: type }));
  };

  getParticipantOptions = () => {
    const { participants, cityFilter, typeFilter } = this.state;
    return participants
      .filter(p => (cityFilter ? p.city === cityFilter : true))
      .filter(p => (typeFilter ? p.type === typeFilter : true))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
      .map(p => ({ value: p.id, label: p.name }));
  };

  // ───────── Group a participant's flat results[] into equipment rows ─────────
  groupResultsByEquipment = participant => {
    const grouped = {};

    (participant.results || []).forEach(r => {
      const key = (r.instrument_name || "").toLowerCase() || "unknown";

      if (!grouped[key]) {
        grouped[key] = {
          key: `${participant.id}_${key}`,
          name: r.instrument_name,
          instrument_type: r.instrument_type,
          manufacturer: r.manufacturer,
          participantName: participant.name,
          rounds: [],
          roundIdSet: new Set(),
          roundDateMap: {}, // round_id -> round_closing_date
          analytesByRound: {}, // round_id -> Set(analyte_id)
        };
      }

      const group = grouped[key];

      group.rounds.push({
        id: `${r.round_id}`,
        name: r.round_name,
        lab_id: participant.id,
        scheme_name: r.scheme_name,
        cycle_no: r.cycle_no,
        round_no: r.round_no,
        analyte_name: r.analyte_name,
        unit: r.unit,
        method: r.method,
        reagent: r.reagent,
        evaluation_status: r.evaluation,
        is_qualitative: r.is_qualitative,
        round_start_date: r.round_start_date,
        round_closing_date: r.round_closing_date,
        round_status: r.round_status,
        instrument_name: r.instrument_name,
        instrument_id: r.instrument_id,
      });

      group.roundIdSet.add(r.round_id);

      if (r.round_closing_date) {
        group.roundDateMap[r.round_id] = r.round_closing_date;
      }

      if (r.analyte_id != null) {
        if (!group.analytesByRound[r.round_id]) {
          group.analytesByRound[r.round_id] = new Set();
        }
        group.analytesByRound[r.round_id].add(r.analyte_id);
      }
    });

    return Object.values(grouped).map(eq => {
      const round_count = eq.roundIdSet.size;

      let lastRoundId = null;
      let lastRoundDate = null;

      Object.entries(eq.roundDateMap).forEach(([roundId, date]) => {
        if (!lastRoundDate || new Date(date) > new Date(lastRoundDate)) {
          lastRoundDate = date;
          lastRoundId = roundId;
        }
      });

      const last_round_count = lastRoundId
        ? eq.analytesByRound[lastRoundId]?.size || 0
        : 0;

      return {
        key: eq.key,
        name: eq.name,
        lab_id: participant.id,
        instrument_type: eq.instrument_type,
        manufacturer: eq.manufacturer,
        participantName: eq.participantName,
        rounds: eq.rounds,
        round_count,
        last_round_id: lastRoundId,
        last_round_date: lastRoundDate,
        last_round_count,
      };
    });
  };

  // ───────── Table data ─────────
  getEquipmentTableData = () => {
    const { participants, cityFilter, typeFilter, participantFilter } =
      this.state;

    let data;

    if (participantFilter) {
      const participant = participants.find(
        p => String(p.id) === String(participantFilter)
      );
      data = participant ? this.groupResultsByEquipment(participant) : [];
    } else {
      data = participants
        .filter(p => (cityFilter ? p.city === cityFilter : true))
        .filter(p => (typeFilter ? p.type === typeFilter : true))
        .flatMap(p => this.groupResultsByEquipment(p));
    }

    return data.sort((a, b) =>
      (a.participantName || "").localeCompare(b.participantName || "")
    );
  };

  render() {
    const { cityFilter, typeFilter, participantFilter } = this.state;
    const equipmentData = this.getEquipmentTableData();

    return (
      <div className="page-content">
        <MetaTags>
          <title>Participant Payment | NHS NEQAS</title>
        </MetaTags>

        <Container fluid>
          <Breadcrumbs title="Labs" breadcrumbItem="EQUIPMENT REPORT - 1" />
          <Row className="justify-content-center align-item-center">
            <Col lg="10">
              <Card>
                <CardBody>
                  <Row className="mb-3">
                    <Col md={4}>
                      <Label for="cityFilter">City</Label>
                      <Select
                        id="cityFilter"
                        options={[
                          { value: "All", label: "All Cities" },
                          ...this.getCityOptions(),
                        ]}
                        value={
                          cityFilter
                            ? { value: cityFilter, label: cityFilter }
                            : { value: "All", label: "All Cities" }
                        }
                        onChange={this.handleCityChange}
                        isClearable
                      />
                    </Col>

                    <Col md={4}>
                      <Label for="typeFilter">Type</Label>
                      <Select
                        id="typeFilter"
                        options={[
                          { value: "All", label: "All Types" },
                          ...this.getTypeOptions(),
                        ]}
                        value={
                          typeFilter
                            ? { value: typeFilter, label: typeFilter }
                            : { value: "All", label: "All Types" }
                        }
                        onChange={this.handleTypeChange}
                        isClearable
                      />
                    </Col>

                    <Col md={4}>
                      <Label for="participantFilter">Participant</Label>
                      <Select
                        id="participantFilter"
                        options={[
                          { value: "All", label: "All Participants" },
                          ...this.getParticipantOptions(),
                        ]}
                        value={
                          participantFilter
                            ? this.getParticipantOptions().find(
                                o =>
                                  String(o.value) === String(participantFilter)
                              ) || null
                            : { value: "All", label: "All Participants" }
                        }
                        onChange={this.handleParticipantChange}
                        placeholder="All Participants"
                        isClearable
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col xl="12">
                      <div className="table-responsive">
                        <BootstrapTable
                          keyField="key"
                          columns={this.equipmentTableColumns}
                          data={equipmentData}
                          defaultSorted={this.defaultSorted}
                          classes={
                            "table align-middle table-condensed table-hover"
                          }
                          bordered={false}
                          striped={false}
                          headerWrapperClasses={"table-header-sky-blue"}
                          responsive
                          noDataIndication={() => "No equipment submitted"}
                        />
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }
}

ParticipantPayments.propTypes = {
  InstrumentReportLabDetail: PropTypes.object,
  onGetParticipantpayment: PropTypes.func,
  match: PropTypes.object,
  history: PropTypes.object,
  onGetInstrumentReportList: PropTypes.func,
};

const mapStateToProps = state => {
  const InstrumentReportLabDetail =
    state.ListUnit?.InstrumentReportLabDetail || {};

  return {
    userID: state.Account?.userID || null,
    InstrumentReportLabDetail,
  };
};

const mapDispatchToProps = dispatch => ({
  onGetParticipantpayment: id => dispatch(getParticipantPayment(id)),
  onupdatePayment: payload => dispatch(updatePayment(payload)),
  ondeletePayment: payload => dispatch(deletePayment(payload)),
  onGetInstrumentReportList: id => dispatch(getInstrumentReportLab(id)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ParticipantPayments);
